/**
 * CONTRIBUTIONS QUEUE (EXPERT-ROOMS.md §16) — on the registry's Review tab (curators only): what companies offered
 * back to the experts the registry publishes — already scrubbed, anonymous. Accept (fold it into the next version of
 * the expert) or decline, with a note the contributor sees.
 */
import React, { useEffect, useState } from 'react';
import { listContributions, decideContribution } from '@stepflow/ui-shared/services/networkRegistryApi';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

export default function RegistryContributionsPanel() {
  const [rows, setRows] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState('');
  const load = () => listContributions('pending').then((d) => setRows(d.contributions || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows || !rows.length) return null;

  const decide = async (c, decision) => {
    setBusy(c.contributionId);
    try {
      await decideContribution(c.contributionId, c.registryNetworkId, decision, notes[c.contributionId] || '');
      await load();
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="registry-contributions">
      <h3 className="text-sm font-semibold text-gray-800">Contributions ({rows.length})</h3>
      <p className="text-xs text-gray-500">What companies offered back to experts you publish — scrubbed, anonymous.</p>
      <ul className="mt-2 space-y-2">
        {rows.map((c) => (
          <li key={c.contributionId} className="rounded-md border border-gray-200 bg-white p-2 text-xs" data-testid={`contribution-${c.contributionId}`}>
            <div className="font-medium text-gray-800">{c.registryNetworkId} · base v{c.baseVersion} · {(c.items || []).length} item(s)</div>
            {c.note ? <div className="text-gray-600">“{c.note}”</div> : null}
            <ul className="mt-1 space-y-1">
              {(c.items || []).map((i) => (
                <li key={i.id}>
                  <span className="font-medium">{i.kind === 'procedure' ? 'Procedure' : 'Rule'} — {i.title}</span>
                  <pre className="whitespace-pre-wrap font-sans text-[11px] text-gray-700">{i.text}</pre>
                </li>
              ))}
            </ul>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input value={notes[c.contributionId] || ''} onChange={(e) => setNotes({ ...notes, [c.contributionId]: e.target.value })}
                placeholder="Note to the contributor" className="flex-1 rounded border border-gray-200 px-2 py-0.5" />
              <button type="button" disabled={busy === c.contributionId} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
                data-testid={`contribution-accept-${c.contributionId}`} onClick={() => decide(c, 'accepted')}>Accept</button>
              <button type="button" disabled={busy === c.contributionId} className={`${btn} text-rose-700 hover:bg-rose-50`}
                data-testid={`contribution-decline-${c.contributionId}`} onClick={() => decide(c, 'declined')}>Decline</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
